/**
 * VenuesPanel — Admin CRUD for table_archive_overlays.
 *
 * Mounted as the "Venues catalogue" tab in /admin. Mirror of the existing
 * Clubs catalogue panel pattern. Filter by Michelin distinction · city ·
 * verification status; search by name; row click → drawer with toggle to
 * flip human_confirmed on/off; audit trail.
 *
 * Founder spec (11 Jun 2026): unify Michelin verification under /admin.
 */

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const DISTINCTION_PILLS = [
  { key: "1star", label: "★", help: "Michelin 1-star" },
  { key: "2star", label: "★★", help: "Michelin 2-star" },
  { key: "3star", label: "★★★", help: "Michelin 3-star" },
  { key: "bib", label: "BIB", help: "Bib Gourmand" },
];

const VERIFICATION_PILLS = [
  { key: "confirmed", label: "Confirmed by Concierge®" },
  { key: "unconfirmed", label: "Awaiting confirmation" },
  { key: "rejected", label: "Rejected" },
];

export default function VenuesPanel() {
  const [facets, setFacets] = useState(null);
  const [list, setList] = useState({ total: 0, items: [], page: 1, limit: 50 });
  const [loading, setLoading] = useState(false);

  // Filters
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [distinction, setDistinction] = useState("");
  const [verification, setVerification] = useState("");
  const [page, setPage] = useState(1);
  const limit = 25;

  // Drawer
  const [open, setOpen] = useState(null); // venue id
  const [drawerDoc, setDrawerDoc] = useState(null);
  const [savingDrawer, setSavingDrawer] = useState(false);
  const [drawerMsg, setDrawerMsg] = useState(null);

  // Top-level msg
  const [msg, setMsg] = useState(null);

  const loadFacets = useCallback(async () => {
    try {
      const { data } = await api.get("/console/venues/facets");
      setFacets(data);
    } catch (e) {
      setMsg({ ok: false, text: formatApiError(e.response?.data?.detail) });
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (q) params.q = q;
      if (city) params.city = city;
      if (distinction) params.distinction = distinction;
      if (verification) params.verification = verification;
      const { data } = await api.get("/console/venues", { params });
      setList(data);
    } catch (e) {
      setMsg({ ok: false, text: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  }, [q, city, distinction, verification, page]);

  useEffect(() => { loadFacets(); }, [loadFacets]);
  useEffect(() => { loadList(); }, [loadList]);

  function resetFilters() {
    setQ(""); setCity(""); setDistinction(""); setVerification(""); setPage(1);
  }

  async function openDrawer(id) {
    setOpen(id);
    setDrawerDoc(null);
    setDrawerMsg(null);
    try {
      const { data } = await api.get(`/console/venues/${id}`);
      setDrawerDoc(data);
    } catch (e) {
      setDrawerMsg({ ok: false, text: formatApiError(e.response?.data?.detail) });
    }
  }

  async function saveDrawer(patch) {
    if (!drawerDoc) return;
    setSavingDrawer(true);
    setDrawerMsg(null);
    try {
      const { data } = await api.patch(`/console/venues/${drawerDoc.id}`, patch);
      setDrawerDoc(data.venue || drawerDoc);
      setDrawerMsg({ ok: true, text: data.no_changes ? "No changes to save." : "Saved." });
      loadFacets();
      loadList();
    } catch (e) {
      setDrawerMsg({ ok: false, text: formatApiError(e.response?.data?.detail) });
    } finally {
      setSavingDrawer(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(list.total / limit));

  return (
    <div data-testid="admin-venues-panel" className="space-y-8">
      {/* Header */}
      <header>
        <p className="eyebrow">Venues · Michelin & restaurants</p>
        <h2 className="font-serif text-3xl md:text-4xl mt-2">Venues catalogue</h2>
        <p className="mt-3 text-[14px] text-[hsl(var(--muted))] max-w-2xl">
          Filter by Michelin distinction or city, search by name, click any row to open
          the verification drawer. Confirmed rows show on /table; unconfirmed ones don&apos;t.
        </p>
      </header>

      {msg && (
        <div className={`p-3 rounded-md text-[13px] ${msg.ok ? "bg-emerald-50 text-emerald-900" : "bg-rose-50 text-rose-900"}`}>
          {msg.text}
        </div>
      )}

      {/* Facets row */}
      {facets && (
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
          <Stat label="Total" value={facets.total} testid="venues-stat-total" />
          <Stat label="★" value={facets.distinction["1star"]} testid="venues-stat-1star" />
          <Stat label="★★" value={facets.distinction["2star"]} testid="venues-stat-2star" />
          <Stat label="★★★" value={facets.distinction["3star"]} testid="venues-stat-3star" />
          <Stat label="BIB" value={facets.distinction.bib} testid="venues-stat-bib" />
          <Stat label="Confirmed" value={facets.verification.confirmed} accent="brass" testid="venues-stat-confirmed" />
          <Stat label="Awaiting" value={facets.verification.unconfirmed} testid="venues-stat-unconfirmed" />
        </div>
      )}

      {/* Filter row */}
      <div className="space-y-3 p-4 rounded-md border border-[hsl(var(--brass)/0.25)] bg-white/50">
        <div className="flex gap-2 items-center flex-wrap">
          <input
            type="text" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search a venue name…"
            data-testid="venues-search"
            className="px-3 py-2 rounded-md border border-[hsl(var(--brass)/0.3)] bg-white text-[14px] flex-1 min-w-[180px]"
          />
          <button onClick={resetFilters} data-testid="venues-reset"
                  className="text-[11px] tracking-[0.14em] uppercase px-3 py-2 rounded-md border border-[hsl(var(--brass)/0.5)] text-[hsl(var(--navy))] hover:bg-[hsl(var(--brass)/0.10)]">
            Reset
          </button>
          <button onClick={async () => {
            // FIR-20260612-005 · CSV export with the active filter set
            // applied. Default `full=true` ships every field on every
            // matched doc (149 columns today). Use ?full=false for the
            // 18-column compact shape.
            try {
              const qs = new URLSearchParams();
              if (q) qs.set("q", q);
              if (city) qs.set("city", city);
              if (distinction) qs.set("distinction", distinction);
              if (verification) qs.set("verification", verification);
              const res = await api.get(`/console/venues/export-csv?${qs.toString()}`, { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
              const a = document.createElement("a");
              a.href = url;
              const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
              a.download = `venues-${ts}.csv`;
              document.body.appendChild(a); a.click(); a.remove();
              window.URL.revokeObjectURL(url);
              setMsg({ ok: true, text: `Export downloaded · ${list.total.toLocaleString()} venues` });
            } catch (e) {
              setMsg({ ok: false, text: formatApiError(e?.response?.data?.detail) || e.message });
            }
          }} data-testid="venues-export-button"
                  className="text-[11px] tracking-[0.14em] uppercase px-3 py-2 rounded-md bg-[hsl(var(--navy))] text-white hover:bg-[hsl(var(--navy)/0.85)]">
            Export CSV
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="eyebrow mr-2">Michelin</span>
          {DISTINCTION_PILLS.map((d) => (
            <Chip key={d.key} active={distinction === d.key}
                  onClick={() => { setDistinction(distinction === d.key ? "" : d.key); setPage(1); }}
                  testid={`venues-chip-${d.key}`}>
              {d.label}{facets?.distinction?.[d.key === "bib" ? "bib" : d.key] !== undefined ? ` · ${facets.distinction[d.key === "bib" ? "bib" : d.key]}` : ""}
            </Chip>
          ))}
        </div>

        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="eyebrow mr-2">Verification</span>
          {VERIFICATION_PILLS.map((d) => (
            <Chip key={d.key} active={verification === d.key}
                  onClick={() => { setVerification(verification === d.key ? "" : d.key); setPage(1); }}
                  testid={`venues-chip-verif-${d.key}`}>
              {d.label}{facets?.verification?.[d.key] !== undefined ? ` · ${facets.verification[d.key]}` : ""}
            </Chip>
          ))}
        </div>

        {facets?.top_destinations && (
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="eyebrow mr-2">City</span>
            {facets.top_destinations.slice(0, 10).map((c) => (
              <Chip key={c.destination} active={city === c.destination}
                    onClick={() => { setCity(city === c.destination ? "" : c.destination); setPage(1); }}
                    testid={`venues-chip-city-${c.destination.toLowerCase().replace(/\W+/g, "-")}`}>
                {c.destination} · {c.count}
              </Chip>
            ))}
          </div>
        )}
      </div>

      {/* Results table */}
      <div className="overflow-x-auto rounded-md border border-[hsl(var(--brass)/0.25)]">
        <table className="w-full text-[13px]" data-testid="venues-table" style={{ borderCollapse: "collapse" }}>
          <thead style={{ background: "hsl(var(--brass) / 0.06)" }}>
            <tr>
              <Th>Name</Th><Th>Destination</Th><Th>Michelin</Th><Th>Confirmed</Th><Th>Confirmed by</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="py-6 text-center text-[hsl(var(--muted))]">Loading…</td></tr>
            )}
            {!loading && list.items.length === 0 && (
              <tr><td colSpan={5} className="py-6 text-center text-[hsl(var(--muted))]">No venues match these filters.</td></tr>
            )}
            {!loading && list.items.map((v) => (
              <tr key={v.id} onClick={() => openDrawer(v.id)}
                  data-testid={`venues-row-${v.id}`}
                  style={{ cursor: "pointer", borderTop: "1px solid hsl(var(--brass) / 0.15)" }}
                  className="hover:bg-[hsl(var(--brass)/0.06)]">
                <td className="py-2.5 px-3 align-top">
                  <div style={{ fontWeight: 600, color: "hsl(var(--navy))" }}>
                    {v.resolved_name || v.original_archive_name || "—"}
                  </div>
                  {(v.resolved_name && v.original_archive_name && v.resolved_name !== v.original_archive_name) && (
                    <div className="text-[11px] text-[hsl(var(--muted))] italic">aka {v.original_archive_name}</div>
                  )}
                </td>
                <td className="py-2.5 px-3 align-top text-[hsl(var(--muted))]">{v.destination || "—"}</td>
                <td className="py-2.5 px-3 align-top">
                  <MichelinPill stars={v.michelin_stars} distinction={v.michelin_distinction} />
                </td>
                <td className="py-2.5 px-3 align-top">
                  {v.human_confirmed ? (
                    <span style={{ background: "hsl(var(--brass) / 0.15)", color: "hsl(var(--brass))",
                                   padding: "2px 8px", borderRadius: 100, fontSize: 11, fontWeight: 700,
                                   letterSpacing: "0.06em" }}>✓ YES</span>
                  ) : v.rejected ? (
                    <span style={{ color: "#b4441f", fontSize: 11.5, fontWeight: 600 }}>REJECTED</span>
                  ) : (
                    <span style={{ color: "hsl(var(--muted))", fontSize: 11.5 }}>—</span>
                  )}
                </td>
                <td className="py-2.5 px-3 align-top text-[11.5px] text-[hsl(var(--muted))]"
                    style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {v.confirmed_by || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {list.total > limit && (
        <div className="flex items-center gap-3 justify-center text-[12px]">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
                  data-testid="venues-prev"
                  className="px-3 py-1.5 rounded-md border border-[hsl(var(--brass)/0.5)] disabled:opacity-30">← Prev</button>
          <span className="text-[hsl(var(--muted))]">Page {page} of {totalPages} · {list.total.toLocaleString()} venues</span>
          <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}
                  data-testid="venues-next"
                  className="px-3 py-1.5 rounded-md border border-[hsl(var(--brass)/0.5)] disabled:opacity-30">Next →</button>
        </div>
      )}

      {/* Drawer */}
      {open && (
        <VenueDrawer
          venue={drawerDoc}
          saving={savingDrawer}
          msg={drawerMsg}
          onClose={() => { setOpen(null); setDrawerDoc(null); setDrawerMsg(null); }}
          onSave={saveDrawer}
        />
      )}
    </div>
  );
}

/* ─── Drawer ──────────────────────────────────────── */

function VenueDrawer({ venue, saving, msg, onClose, onSave }) {
  const [distinction, setDistinction] = useState(venue?.michelin_distinction || "");
  const [stars, setStars] = useState(venue?.michelin_stars ?? "");
  const [knownFor, setKnownFor] = useState(venue?.known_for || "");
  const [whisper, setWhisper] = useState(venue?.concierge_whisper || "");

  useEffect(() => {
    if (venue) {
      setDistinction(venue.michelin_distinction || "");
      setStars(venue.michelin_stars ?? "");
      setKnownFor(venue.known_for || "");
      setWhisper(venue.concierge_whisper || "");
    }
  }, [venue]);

  if (!venue) {
    return (
      <Backdrop onClose={onClose}>
        <div className="text-[hsl(var(--muted))]">Loading venue…</div>
      </Backdrop>
    );
  }

  function commit(extra = {}) {
    const patch = { ...extra };
    if (distinction !== (venue.michelin_distinction || "")) patch.michelin_distinction = distinction || null;
    if (`${stars}` !== `${venue.michelin_stars ?? ""}`) patch.michelin_stars = stars === "" ? null : Number(stars);
    if (knownFor !== (venue.known_for || "")) patch.known_for = knownFor;
    if (whisper !== (venue.concierge_whisper || "")) patch.concierge_whisper = whisper;
    onSave(patch);
  }

  return (
    <Backdrop onClose={onClose}>
      <div data-testid="venues-drawer" className="max-w-2xl w-full bg-white rounded-md shadow-2xl"
           style={{ maxHeight: "90vh", overflowY: "auto" }}>
        <div className="p-6 border-b border-[hsl(var(--brass)/0.25)] flex items-start justify-between gap-3">
          <div>
            <p className="eyebrow">Venue · {venue.destination || "—"}</p>
            <h3 className="font-serif text-2xl mt-1">{venue.resolved_name || venue.original_archive_name || "Unnamed"}</h3>
            {venue.resolved_address && (
              <p className="text-[12px] text-[hsl(var(--muted))] mt-1">{venue.resolved_address}</p>
            )}
            {venue.place_id && (
              <p className="text-[10.5px] text-[hsl(var(--muted))] mt-1 font-mono">place_id · {venue.place_id}</p>
            )}
          </div>
          <button onClick={onClose} data-testid="venues-drawer-close"
                  className="text-[11px] tracking-[0.14em] uppercase px-3 py-1.5 rounded-full border border-[hsl(var(--brass)/0.5)]">
            Close
          </button>
        </div>

        <div className="p-6 space-y-6">
          {msg && (
            <div className={`p-3 rounded-md text-[13px] ${msg.ok ? "bg-emerald-50 text-emerald-900" : "bg-rose-50 text-rose-900"}`}>
              {msg.text}
            </div>
          )}

          {/* Verification toggle */}
          <div className="rounded-md p-4 border border-[hsl(var(--brass)/0.3)] bg-[hsl(var(--brass)/0.05)]">
            <div className="eyebrow mb-2">Concierge® verification</div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[13px]">
                {venue.human_confirmed ? (
                  <span>
                    <b className="text-[hsl(var(--brass))]">✓ Confirmed</b>
                    {venue.confirmed_by && <span className="text-[hsl(var(--muted))]"> · by {venue.confirmed_by}</span>}
                    {venue.verified_at && <span className="text-[hsl(var(--muted))]"> · {new Date(venue.verified_at).toLocaleDateString()}</span>}
                  </span>
                ) : venue.rejected ? (
                  <span style={{ color: "#7a2c12" }}><b>Rejected</b></span>
                ) : (
                  <span className="text-[hsl(var(--muted))]">Not yet confirmed</span>
                )}
              </div>
              <div className="flex gap-2">
                {!venue.human_confirmed && (
                  <button data-testid="venues-drawer-confirm"
                          onClick={() => commit({ human_confirmed: true })} disabled={saving}
                          className="px-4 py-2 rounded-md bg-[hsl(var(--brass))] text-white text-[12px] tracking-[0.1em] uppercase font-semibold disabled:opacity-50">
                    Confirm
                  </button>
                )}
                {venue.human_confirmed && (
                  <button data-testid="venues-drawer-unconfirm"
                          onClick={() => commit({ human_confirmed: false })} disabled={saving}
                          className="px-4 py-2 rounded-md border border-[hsl(var(--brass)/0.5)] text-[12px] tracking-[0.1em] uppercase font-semibold disabled:opacity-50">
                    Un-confirm
                  </button>
                )}
                {!venue.rejected && !venue.human_confirmed && (
                  <button data-testid="venues-drawer-reject"
                          onClick={() => commit({ rejected: true })} disabled={saving}
                          className="px-4 py-2 rounded-md border border-rose-300 text-rose-700 text-[12px] tracking-[0.1em] uppercase font-semibold disabled:opacity-50">
                    Reject
                  </button>
                )}
              </div>
            </div>
          </div>

          <Field label="Michelin distinction">
            <select value={distinction} onChange={(e) => setDistinction(e.target.value)}
                    data-testid="venues-drawer-distinction"
                    className="w-full px-3 py-2 rounded-md border border-[hsl(var(--brass)/0.3)] bg-white text-[14px]">
              <option value="">— none —</option>
              <option value="michelin_star">Michelin star</option>
              <option value="bib_gourmand">Bib Gourmand</option>
            </select>
          </Field>

          <Field label="Stars (0–3)">
            <input type="number" min={0} max={3} value={stars}
                   onChange={(e) => setStars(e.target.value)}
                   data-testid="venues-drawer-stars"
                   className="w-32 px-3 py-2 rounded-md border border-[hsl(var(--brass)/0.3)] bg-white text-[14px]" />
          </Field>

          <Field label="Known for">
            <textarea value={knownFor} onChange={(e) => setKnownFor(e.target.value)} rows={2}
                      data-testid="venues-drawer-known-for"
                      className="w-full px-3 py-2 rounded-md border border-[hsl(var(--brass)/0.3)] bg-white text-[13px]" />
          </Field>

          <Field label="Concierge® whisper">
            <textarea value={whisper} onChange={(e) => setWhisper(e.target.value)} rows={3}
                      data-testid="venues-drawer-whisper"
                      className="w-full px-3 py-2 rounded-md border border-[hsl(var(--brass)/0.3)] bg-white text-[13px]" />
          </Field>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 rounded-md border border-[hsl(var(--brass)/0.5)] text-[12px] tracking-[0.1em] uppercase">
              Cancel
            </button>
            <button onClick={() => commit()} disabled={saving} data-testid="venues-drawer-save"
                    className="px-5 py-2 rounded-md bg-[hsl(var(--navy))] text-white text-[12px] tracking-[0.1em] uppercase font-semibold disabled:opacity-50">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>

          {/* Audit trail */}
          {Array.isArray(venue.admin_edit_log) && venue.admin_edit_log.length > 0 && (
            <div className="pt-4 border-t border-[hsl(var(--brass)/0.2)]">
              <div className="eyebrow mb-2">Audit trail · latest 8</div>
              <ul className="space-y-1 text-[11.5px] text-[hsl(var(--muted))]">
                {venue.admin_edit_log.slice(-8).reverse().map((e, i) => (
                  <li key={i}>
                    <span className="font-mono">{new Date(e.at).toLocaleString()}</span>
                    {" · "}<b style={{ color: "hsl(var(--navy))" }}>{e.field}</b>
                    {" "}<span style={{ color: "#7a2c12" }}>{JSON.stringify(e.old)}</span>
                    {" → "}<span style={{ color: "hsl(var(--brass))" }}>{JSON.stringify(e.new)}</span>
                    {" "}<span className="italic">by {e.by}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Backdrop>
  );
}

function Backdrop({ children, onClose }) {
  return (
    <div onClick={onClose}
         className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(15, 23, 42, 0.55)", backdropFilter: "blur(2px)" }}>
      <div onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}

/* ─── Tiny primitives ──────────────────────────────────────── */

function Stat({ label, value, accent, testid }) {
  return (
    <div data-testid={testid}
         className="p-3 rounded-md border border-[hsl(var(--brass)/0.25)] bg-white/60">
      <div className="text-[10px] tracking-[0.14em] uppercase text-[hsl(var(--muted))]">{label}</div>
      <div className="font-serif text-2xl mt-0.5"
           style={{ color: accent === "brass" ? "hsl(var(--brass))" : "hsl(var(--navy))" }}>
        {(value ?? 0).toLocaleString()}
      </div>
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            className="text-[11px] tracking-[0.06em] px-2.5 py-1 rounded-full border transition"
            style={{
              background: active ? "hsl(var(--brass))" : "transparent",
              borderColor: active ? "hsl(var(--brass))" : "hsl(var(--brass) / 0.4)",
              color: active ? "hsl(var(--ivory))" : "hsl(var(--navy))",
              fontWeight: active ? 700 : 500,
            }}>
      {children}
    </button>
  );
}

function Th({ children }) {
  return (
    <th className="text-left py-2 px-3 text-[10.5px] tracking-[0.14em] uppercase font-medium text-[hsl(var(--muted))]">
      {children}
    </th>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10.5px] tracking-[0.14em] uppercase text-[hsl(var(--muted))] mb-1.5 font-medium">{label}</label>
      {children}
    </div>
  );
}

function MichelinPill({ stars, distinction }) {
  if (stars && stars > 0) {
    return (
      <span className="font-serif" style={{ color: "hsl(var(--brass))", fontSize: 14, letterSpacing: 1 }}>
        {"★".repeat(stars)}
      </span>
    );
  }
  if (distinction === "bib_gourmand") {
    return (
      <span style={{ background: "hsl(var(--brass) / 0.12)", color: "hsl(var(--brass))",
                     padding: "1px 8px", borderRadius: 100, fontSize: 10, fontWeight: 700,
                     letterSpacing: "0.08em" }}>
        BIB
      </span>
    );
  }
  return <span style={{ color: "hsl(var(--muted))", fontSize: 11 }}>—</span>;
}
